import React from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

/**
 * PageTransition
 *
 * Wraps the active route in a Framer Motion `AnimatePresence` so each route
 * change runs a short fade + slight upward slide.
 *
 * Timings tuned to feel "premium adtech" — fast but not abrupt:
 *   - exit:  180ms ease-out
 *   - enter: 360ms easeOut, y: 12px → 0, opacity 0 → 1
 *
 * Accessibility:
 *   - Honors `prefers-reduced-motion` by collapsing to a simple opacity tween.
 */
export default function PageTransition() {
  const location = useLocation();
  const outlet = useOutlet();
  const prefersReducedMotion = useReducedMotion();

  const initial = prefersReducedMotion
    ? { opacity: 0 }
    : { opacity: 0, y: 12 };
  const animate = prefersReducedMotion
    ? { opacity: 1, transition: { duration: 0.18, ease: 'easeOut' } }
    : {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.36,
          ease: [0.22, 1, 0.36, 1], // smooth easeOut curve
        },
      };
  const exit = prefersReducedMotion
    ? { opacity: 0, transition: { duration: 0.12 } }
    : { opacity: 0, y: -6, transition: { duration: 0.18, ease: 'easeIn' } };

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={initial}
        animate={animate}
        exit={exit}
        // Avoid layout shift while transitioning out
        style={{ willChange: 'opacity, transform' }}
        data-testid="page-transition-wrapper"
      >
        {outlet}
      </motion.div>
    </AnimatePresence>
  );
}
